import useFetchData from 'app/hooks/useFetchData';
import { approvalOrderApi } from 'app/services/orderApi';
import { useCallback, useState } from 'react';

interface useApproveOrderProps {
  acceptOrder?: {
    onBefore?: () => void;
    onSuccess?: (response) => void;
    onError?: (error) => void;
    onFinally?: () => void;
  };
  rejectOrder?: {
    onBefore?: () => void;
    onSuccess?: (response) => void;
    onError?: (error) => void;
    onFinally?: () => void;
  };
}

const useApproveOrder = (props?: useApproveOrderProps) => {
  const acceptOrder = props?.acceptOrder;
  const rejectOrder = props?.rejectOrder;

  const [approvalType, setApprovalType] = useState<'ACCEPT' | 'REJECT' | null>(
    null,
  );

  const { onFetchData: onApprovalOrder } = useFetchData({
    actionApi: approvalOrderApi,
    IIFE: false,
  });

  const handleAcceptOrder = useCallback(
    (idHash: string) => {
      if (approvalType) return;

      setApprovalType('ACCEPT');

      const formData = new FormData();
      formData.append('idHash', idHash);
      formData.append('state', 'confirmed');

      onApprovalOrder({
        payloadData: formData,
        callback: {
          onBefore: acceptOrder?.onBefore,
          onSuccess: acceptOrder?.onSuccess,
          onError: acceptOrder?.onError,
          onFinally: () => {
            setApprovalType(null);
            acceptOrder?.onFinally?.();
          },
        },
      });
    },
    [acceptOrder, approvalType, onApprovalOrder],
  );

  const handleRejectOrder = useCallback(
    (idHash: string) => {
      if (approvalType) return;

      setApprovalType('REJECT');

      const formData = new FormData();
      formData.append('idHash', idHash);
      formData.append('state', 'unconfirmed');

      onApprovalOrder({
        payloadData: formData,
        callback: {
          onBefore: rejectOrder?.onBefore,
          onSuccess: rejectOrder?.onSuccess,
          onError: rejectOrder?.onError,
          onFinally: () => {
            setApprovalType(null);
            rejectOrder?.onFinally?.();
          },
        },
      });
    },
    [approvalType, onApprovalOrder, rejectOrder],
  );

  return {
    approvalType,
    handleAcceptOrder,
    handleRejectOrder,
  };
};

export default useApproveOrder;
